"use client";

import { useState, useCallback, useMemo, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  Grid3x3,
  List,
  Search,
  X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { get } from "../../utils/service";
import Drawer from "@/components/ui/Drawer";

interface Meeting {
  id: string;
  title: string;
  description: string;
  contact_date: string;
  contact_time_start: string;
  contact_time_end: string | null;
  contacted_by_first_name: string;
  contacted_by_last_name: string;
  contacted_by_email: string;
  contacted_by_phone: string;
  lead_company_name: string;
  lead_contact_person: string;
  lead_email: string;
  lead_phone: string;
  lead_location: string;
  lead_industry: string;
  lead_type: string;
  lead_current_stage: number;
  lead_status: string;
  created_at: string;
}

interface MeetingsResponse {
  success: boolean;
  data: {
    meetings: Meeting[];
    month: number;
    year: number;
    total: number;
  };
}

interface Event {
  id: string;
  title: string;
  description?: string;
  startTime: Date;
  endTime: Date;
  color: string;
  category?: string;
  attendees?: string[];
  tags?: string[];
  meeting?: Meeting;
}

const defaultColors = [
  {
    name: "Orange",
    value: "orange",
    bg: "bg-[#ff6d27]",
    text: "text-[#ff6d27]",
  },
  {
    name: "Light Orange",
    value: "light-orange",
    bg: "bg-[#FFA347]",
    text: "text-[#FFA347]",
  },
  {
    name: "Dark Orange",
    value: "dark-orange",
    bg: "bg-[#E67E00]",
    text: "text-[#E67E00]",
  },
  { name: "Peach", value: "peach", bg: "bg-[#FFB366]", text: "text-[#FFB366]" },
  { name: "Amber", value: "amber", bg: "bg-[#D97F0F]", text: "text-[#D97F0F]" },
  { name: "Gold", value: "gold", bg: "bg-[#CC8500]", text: "text-[#CC8500]" },
];

const convertMeetingsToEvents = (meetings: Meeting[]): Event[] => {
  return meetings.map((meeting, index) => {
    const startDate = new Date(
      `${meeting.contact_date}T${meeting.contact_time_start}`
    );
    const endDate = meeting.contact_time_end
      ? new Date(`${meeting.contact_date}T${meeting.contact_time_end}`)
      : new Date(startDate.getTime() + 60 * 60 * 1000);

    return {
      id: meeting.id,
      title: meeting.title,
      description: meeting.description,
      startTime: startDate,
      endTime: endDate,
      color: defaultColors[index % defaultColors.length].value,
      category: "Meeting",
      attendees: [
        `${meeting.contacted_by_first_name} ${meeting.contacted_by_last_name}`,
      ],
      tags: [`Stage ${meeting.lead_current_stage}`, meeting.lead_status],
      meeting: meeting,
    };
  });
};

const FloatingDrawer = ({
  isOpen,
  onClose,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  position?: "bottom" | "right";
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  const drawerPosition = isMobile || isTablet ? "bottom" : "right";

  if (drawerPosition === "bottom") {
    return (
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        position="bottom"
        heightPercentage={80}
        showCloseButton={false}
      >
        <div className="h-full flex flex-col bg-black text-white">
          {children}
        </div>
      </Drawer>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{
              type: "spring",
              damping: 30,
              stiffness: 300,
            }}
            className="fixed top-4 right-4 bottom-4 w-96 bg-black border border-gray-800 shadow-2xl rounded-2xl z-50 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col bg-black text-white">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

function MeetingDetailsDialog({
  meeting,
  onClose,
}: {
  meeting: Meeting;
  onClose: () => void;
}) {
  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <FloatingDrawer isOpen={true} onClose={onClose}>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Meeting Details</h2>
          <p className="text-xs text-gray-400 mt-1">
            {formatDate(meeting.contact_date)}
          </p>
        </div>
        <button
          onClick={onClose}
          className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-300"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="space-y-4">
          <div>
            <h3 className="text-xs font-semibold text-white mb-3">
              {meeting.title}
            </h3>
            {meeting.description && (
              <p className="text-xs text-gray-400 leading-relaxed">
                {meeting.description}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5">
                Time
              </label>
              <div className="flex items-center gap-2 text-xs text-white">
                <Clock className="h-3.5 w-3.5 text-gray-400" />
                <span>
                  {formatTime(meeting.contact_time_start)}
                  {meeting.contact_time_end &&
                    ` - ${formatTime(meeting.contact_time_end)}`}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5">
                Contacted By
              </label>
              <div className="text-xs text-white">
                {meeting.contacted_by_first_name}{" "}
                {meeting.contacted_by_last_name}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">
                {meeting.contacted_by_email}
              </div>
              <div className="text-xs text-gray-400">
                {meeting.contacted_by_phone}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-6">
          <h3 className="text-xs font-semibold text-white mb-4">
            Client Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5">
                Company
              </label>
              <div className="text-xs text-white">
                {meeting.lead_company_name}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5">
                Contact Person
              </label>
              <div className="text-xs text-white">
                {meeting.lead_contact_person}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5">
                Email
              </label>
              <div className="text-xs text-white">{meeting.lead_email}</div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5">
                Phone
              </label>
              <div className="text-xs text-white">{meeting.lead_phone}</div>
            </div>

            {meeting.lead_location && (
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1.5">
                  Location
                </label>
                <div className="text-xs text-white">
                  {meeting.lead_location}
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5">
                Industry
              </label>
              <div className="text-xs text-white capitalize">
                {meeting.lead_industry?.replace(/_/g, " ")}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5">
                Project Type
              </label>
              <div className="text-xs text-white capitalize">
                {meeting.lead_type?.replace(/_/g, " ")}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5">
                Stage
              </label>
              <div className="text-xs text-white">
                Stage {meeting.lead_current_stage}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-4">
          <div className="text-xs text-gray-500">
            Meeting logged on{" "}
            {new Date(meeting.created_at).toLocaleString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        </div>
      </div>

      <div className="border-t border-gray-800 px-6 py-4">
        <button
          onClick={onClose}
          className="w-full rounded-lg bg-gray-800 px-4 py-2.5 text-xs font-medium text-gray-300 transition-colors hover:bg-gray-700"
        >
          Close
        </button>
      </div>
    </FloatingDrawer>
  );
}

function EventCard({
  event,
  onEventClick,
  onDragStart,
  onDragEnd,
  getColorClasses,
  variant = "default",
}: {
  event: Event;
  onEventClick: (event: Event) => void;
  onDragStart: (event: Event) => void;
  onDragEnd: () => void;
  getColorClasses: (color: string) => { bg: string; text: string };
  variant?: "default" | "compact" | "detailed";
}) {
  const [isHovered, setIsHovered] = useState(false);
  const colorClasses = getColorClasses(event.color);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDuration = () => {
    const diff = event.endTime.getTime() - event.startTime.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (variant === "compact") {
    return (
      <div
        draggable
        onDragStart={() => onDragStart(event)}
        onDragEnd={onDragEnd}
        onClick={() => onEventClick(event)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="relative cursor-pointer"
      >
        <div
          className={`rounded px-1.5 py-0.5 text-[10px] font-medium transition-all duration-300 ${
            colorClasses.bg
          } text-white truncate ${isHovered ? "scale-105 shadow-lg z-10" : ""}`}
        >
          {event.title}
        </div>
        {isHovered && (
          <div className="absolute left-0 top-full z-50 mt-1 w-64">
            <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-xl">
              <div className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-semibold text-xs leading-tight text-gray-900">
                    {event.title}
                  </h4>
                  <div
                    className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${colorClasses.bg}`}
                  />
                </div>
                {event.description && (
                  <p className="text-[10px] text-gray-500 line-clamp-2">
                    {event.description}
                  </p>
                )}
                <div className="flex items-center gap-1 text-[10px] text-gray-500">
                  <Clock className="h-2.5 w-2.5" />
                  <span>
                    {formatTime(event.startTime)} - {formatTime(event.endTime)}
                  </span>
                  <span className="text-[9px]">({getDuration()})</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {event.category && (
                    <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[9px] font-medium text-gray-700">
                      {event.category}
                    </span>
                  )}
                  {event.tags?.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center rounded-full border border-gray-200 bg-white px-1.5 py-0.5 text-[9px] font-medium text-gray-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (variant === "detailed") {
    return (
      <div
        draggable
        onDragStart={() => onDragStart(event)}
        onDragEnd={onDragEnd}
        onClick={() => onEventClick(event)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`cursor-pointer rounded-lg p-3 transition-all duration-300 ${
          colorClasses.bg
        } text-white ${
          isHovered ? "scale-[1.03] shadow-2xl ring-2 ring-white/50" : ""
        }`}
      >
        <div className="font-semibold text-xs">{event.title}</div>
        {event.description && (
          <div className="mt-1 text-[10px] opacity-90 line-clamp-2">
            {event.description}
          </div>
        )}
        <div className="mt-2 flex items-center gap-2 text-[10px] opacity-80">
          <Clock className="h-2.5 w-2.5" />
          {formatTime(event.startTime)} - {formatTime(event.endTime)}
        </div>
        {isHovered && (
          <div className="mt-2 flex flex-wrap gap-1">
            {event.category && (
              <span className="inline-flex items-center rounded-full border border-white/30 bg-white/20 px-1.5 py-0.5 text-[10px] font-medium text-white">
                {event.category}
              </span>
            )}
            {event.tags?.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center rounded-full border border-white/30 bg-white/20 px-1.5 py-0.5 text-[10px] font-medium text-white"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      draggable
      onDragStart={() => onDragStart(event)}
      onDragEnd={onDragEnd}
      onClick={() => onEventClick(event)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative"
    >
      <div
        className={`cursor-pointer rounded px-2 py-1 text-[10px] font-medium transition-all duration-300 ${
          colorClasses.bg
        } text-white ${isHovered ? "scale-105 shadow-lg z-10" : ""}`}
      >
        <div className="truncate">{event.title}</div>
      </div>
      {isHovered && (
        <div className="absolute left-0 top-full z-50 mt-1 w-72">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-xl">
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-semibold leading-tight text-xs text-gray-900">
                  {event.title}
                </h4>
                <div
                  className={`h-3.5 w-3.5 rounded-full flex-shrink-0 ${colorClasses.bg}`}
                />
              </div>
              {event.description && (
                <p className="text-xs text-gray-500">{event.description}</p>
              )}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-[10px] text-gray-500">
                  <Clock className="h-3 w-3" />
                  <span>
                    {formatTime(event.startTime)} - {formatTime(event.endTime)}
                  </span>
                  <span className="text-[9px]">({getDuration()})</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {event.category && (
                    <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-700">
                      {event.category}
                    </span>
                  )}
                  {event.tags?.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center rounded-full border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-gray-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MonthView({
  currentDate,
  events,
  onEventClick,
  onDragStart,
  onDragEnd,
  onDrop,
  getColorClasses,
}: {
  currentDate: Date;
  events: Event[];
  onEventClick: (event: Event) => void;
  onDragStart: (event: Event) => void;
  onDragEnd: () => void;
  onDrop: (date: Date) => void;
  getColorClasses: (color: string) => { bg: string; text: string };
}) {
  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );
  const startDate = new Date(firstDayOfMonth);
  startDate.setDate(startDate.getDate() - startDate.getDay());

  const days = [];
  const currentDay = new Date(startDate);

  for (let i = 0; i < 42; i++) {
    days.push(new Date(currentDay));
    currentDay.setDate(currentDay.getDate() + 1);
  }

  const getEventsForDay = (date: Date) => {
    return events.filter((event) => {
      const eventDate = new Date(event.startTime);
      return (
        eventDate.getDate() === date.getDate() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getFullYear() === date.getFullYear()
      );
    });
  };

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="grid grid-cols-7 border-b border-gray-200">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div
            key={day}
            className="border-r border-gray-200 p-2 text-center text-[10px] font-medium last:border-r-0 sm:text-xs text-gray-600"
          >
            <span className="hidden sm:inline">{day}</span>
            <span className="sm:hidden">{day.charAt(0)}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day, index) => {
          const dayEvents = getEventsForDay(day);
          const isCurrentMonth = day.getMonth() === currentDate.getMonth();
          const isToday = day.toDateString() === new Date().toDateString();

          return (
            <div
              key={index}
              className={`min-h-20 border-b border-r border-gray-200 p-1 transition-colors last:border-r-0 sm:min-h-24 sm:p-2 ${
                !isCurrentMonth ? "bg-gray-50" : ""
              } hover:bg-gray-50`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(day)}
            >
              <div
                className={`mb-1 flex h-4 w-4 items-center justify-center rounded-full text-[10px] sm:h-5 sm:w-5 sm:text-xs ${
                  isToday
                    ? "bg-[#ff6d27] text-white font-semibold"
                    : "text-gray-700"
                }`}
              >
                {day.getDate()}
              </div>
              <div className="space-y-1">
                {dayEvents.slice(0, 3).map((event) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onEventClick={onEventClick}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    getColorClasses={getColorClasses}
                    variant="compact"
                  />
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-[9px] text-gray-500 sm:text-[10px]">
                    +{dayEvents.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekView({
  currentDate,
  events,
  onEventClick,
  onDragStart,
  onDragEnd,
  onDrop,
  getColorClasses,
}: {
  currentDate: Date;
  events: Event[];
  onEventClick: (event: Event) => void;
  onDragStart: (event: Event) => void;
  onDragEnd: () => void;
  onDrop: (date: Date, hour: number) => void;
  getColorClasses: (color: string) => { bg: string; text: string };
}) {
  const startOfWeek = new Date(currentDate);
  startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(startOfWeek);
    day.setDate(startOfWeek.getDate() + i);
    return day;
  });

  const hours = Array.from({ length: 24 }, (_, i) => i);

  const getEventsForDayAndHour = (date: Date, hour: number) => {
    return events.filter((event) => {
      const eventDate = new Date(event.startTime);
      const eventHour = eventDate.getHours();
      return (
        eventDate.getDate() === date.getDate() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getFullYear() === date.getFullYear() &&
        eventHour === hour
      );
    });
  };

  return (
    <div className="overflow-auto rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="grid grid-cols-8 border-b border-gray-200">
        <div className="border-r border-gray-200 p-2 text-center text-[10px] font-medium sm:text-xs text-gray-600">
          Time
        </div>
        {weekDays.map((day) => (
          <div
            key={day.toISOString()}
            className="border-r border-gray-200 p-2 text-center text-[10px] font-medium last:border-r-0 sm:text-xs text-gray-600"
          >
            <div className="hidden sm:block">
              {day.toLocaleDateString("en-US", { weekday: "short" })}
            </div>
            <div className="sm:hidden">
              {day.toLocaleDateString("en-US", { weekday: "narrow" })}
            </div>
            <div className="text-[9px] text-gray-500 sm:text-[10px]">
              {day.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-8">
        {hours.map((hour) => (
          <>
            <div
              key={`time-${hour}`}
              className="border-b border-r border-gray-200 p-1 text-[9px] text-gray-500 sm:p-2 sm:text-[10px]"
            >
              {hour.toString().padStart(2, "0")}:00
            </div>
            {weekDays.map((day) => {
              const dayEvents = getEventsForDayAndHour(day, hour);
              return (
                <div
                  key={`${day.toISOString()}-${hour}`}
                  className="min-h-12 border-b border-r border-gray-200 p-0.5 transition-colors hover:bg-gray-50 last:border-r-0 sm:min-h-16 sm:p-1"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(day, hour)}
                >
                  <div className="space-y-1">
                    {dayEvents.map((event) => (
                      <EventCard
                        key={event.id}
                        event={event}
                        onEventClick={onEventClick}
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                        getColorClasses={getColorClasses}
                        variant="default"
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        ))}
      </div>
    </div>
  );
}

function DayView({
  currentDate,
  events,
  onEventClick,
  onDragStart,
  onDragEnd,
  onDrop,
  getColorClasses,
}: {
  currentDate: Date;
  events: Event[];
  onEventClick: (event: Event) => void;
  onDragStart: (event: Event) => void;
  onDragEnd: () => void;
  onDrop: (date: Date, hour: number) => void;
  getColorClasses: (color: string) => { bg: string; text: string };
}) {
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const getEventsForHour = (hour: number) => {
    return events.filter((event) => {
      const eventDate = new Date(event.startTime);
      const eventHour = eventDate.getHours();
      return (
        eventDate.getDate() === currentDate.getDate() &&
        eventDate.getMonth() === currentDate.getMonth() &&
        eventDate.getFullYear() === currentDate.getFullYear() &&
        eventHour === hour
      );
    });
  };

  return (
    <div className="overflow-auto rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="space-y-0">
        {hours.map((hour) => {
          const hourEvents = getEventsForHour(hour);
          return (
            <div
              key={hour}
              className="flex border-b border-gray-200 last:border-b-0"
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(currentDate, hour)}
            >
              <div className="w-14 flex-shrink-0 border-r border-gray-200 p-2 text-[10px] text-gray-500 sm:w-20 sm:p-3 sm:text-xs">
                {hour.toString().padStart(2, "0")}:00
              </div>
              <div className="min-h-16 flex-1 p-1 transition-colors hover:bg-gray-50 sm:min-h-20 sm:p-2">
                <div className="space-y-2">
                  {hourEvents.map((event) => (
                    <EventCard
                      key={event.id}
                      event={event}
                      onEventClick={onEventClick}
                      onDragStart={onDragStart}
                      onDragEnd={onDragEnd}
                      getColorClasses={getColorClasses}
                      variant="detailed"
                    />
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ListView({
  events,
  onEventClick,
  getColorClasses,
}: {
  events: Event[];
  onEventClick: (event: Event) => void;
  getColorClasses: (color: string) => { bg: string; text: string };
}) {
  const sortedEvents = [...events].sort(
    (a, b) => a.startTime.getTime() - b.startTime.getTime()
  );

  const groupedEvents = sortedEvents.reduce((acc, event) => {
    const dateKey = event.startTime.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(event);
    return acc;
  }, {} as Record<string, Event[]>);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
      <div className="space-y-6">
        {Object.entries(groupedEvents).map(([date, dateEvents]) => (
          <div key={date} className="space-y-3">
            <h3 className="text-[10px] font-semibold text-gray-500 sm:text-xs">
              {date}
            </h3>
            <div className="space-y-2">
              {dateEvents.map((event) => {
                const colorClasses = getColorClasses(event.color);
                return (
                  <div
                    key={event.id}
                    onClick={() => onEventClick(event)}
                    className="group cursor-pointer rounded-lg border border-gray-200 bg-white p-3 transition-all hover:shadow-md hover:scale-[1.01] sm:p-4"
                  >
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div
                        className={`mt-1 h-2 w-2 rounded-full sm:h-2.5 sm:w-2.5 ${colorClasses.bg}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <h4 className="font-semibold text-xs group-hover:text-[#ff6d27] transition-colors text-gray-900 truncate">
                              {event.title}
                            </h4>
                            {event.description && (
                              <p className="mt-1 text-[10px] text-gray-500 sm:text-xs line-clamp-2">
                                {event.description}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {event.category && (
                              <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-700">
                                {event.category}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[9px] text-gray-500 sm:gap-4 sm:text-[10px]">
                          <div className="flex items-center gap-1">
                            <Clock className="h-2.5 w-2.5" />
                            {event.startTime.toLocaleTimeString("en-US", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}{" "}
                            -{" "}
                            {event.endTime.toLocaleTimeString("en-US", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                          {event.tags && event.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {event.tags.map((tag) => (
                                <span
                                  key={tag}
                                  className="inline-flex items-center rounded-full border border-gray-200 bg-white px-1.5 py-0.5 text-[9px] font-medium text-gray-700 sm:text-[10px]"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {sortedEvents.length === 0 && (
          <div className="py-12 text-center text-xs text-gray-500">
            No events found
          </div>
        )}
      </div>
    </div>
  );
}

export function EventManager({
  onEventUpdate,
  colors = defaultColors,
  defaultView = "month",
  className,
}: {
  onEventCreate?: (event: Omit<Event, "id">) => void;
  onEventUpdate?: (id: string, event: Partial<Event>) => void;
  onEventDelete?: (id: string) => void;
  categories?: string[];
  colors?: { name: string; value: string; bg: string; text: string }[];
  defaultView?: "month" | "week" | "day" | "list";
  className?: string;
  availableTags?: string[];
}) {
  const [events, setEvents] = useState<Event[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<"month" | "week" | "day" | "list">(
    defaultView
  );
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [draggedEvent, setDraggedEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchMeetings();
  }, [currentDate]);

  const fetchMeetings = async () => {
    setLoading(true);
    try {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const response = await get<MeetingsResponse>(
        `/meetings/calendar?month=${month}&year=${year}`
      );
      if (response.success) {
        const convertedEvents = convertMeetingsToEvents(response.data.meetings);
        setEvents(convertedEvents);
      }
    } catch (error) {
      console.error("Error fetching meetings:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          event.title.toLowerCase().includes(query) ||
          event.description?.toLowerCase().includes(query) ||
          event.category?.toLowerCase().includes(query) ||
          event.tags?.some((tag) => tag.toLowerCase().includes(query));

        if (!matchesSearch) return false;
      }

      return true;
    });
  }, [events, searchQuery]);

  const handleDragStart = useCallback((event: Event) => {
    setDraggedEvent(event);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedEvent(null);
  }, []);

  const handleDrop = useCallback(
    (date: Date, hour?: number) => {
      if (!draggedEvent) return;

      const duration =
        draggedEvent.endTime.getTime() - draggedEvent.startTime.getTime();
      const newStartTime = new Date(date);
      if (hour !== undefined) {
        newStartTime.setHours(hour, 0, 0, 0);
      }
      const newEndTime = new Date(newStartTime.getTime() + duration);

      const updatedEvent = {
        ...draggedEvent,
        startTime: newStartTime,
        endTime: newEndTime,
      };

      setEvents((prev) =>
        prev.map((e) => (e.id === draggedEvent.id ? updatedEvent : e))
      );
      onEventUpdate?.(draggedEvent.id, updatedEvent);
      setDraggedEvent(null);
    },
    [draggedEvent, onEventUpdate]
  );

  const navigateDate = useCallback(
    (direction: "prev" | "next") => {
      setCurrentDate((prev) => {
        const newDate = new Date(prev);
        if (view === "month") {
          newDate.setMonth(prev.getMonth() + (direction === "next" ? 1 : -1));
        } else if (view === "week") {
          newDate.setDate(prev.getDate() + (direction === "next" ? 7 : -7));
        } else if (view === "day") {
          newDate.setDate(prev.getDate() + (direction === "next" ? 1 : -1));
        }
        return newDate;
      });
    },
    [view]
  );

  const getColorClasses = useCallback(
    (colorValue: string) => {
      const color = colors.find((c) => c.value === colorValue);
      return color || colors[0];
    },
    [colors]
  );

  if (loading) {
    return (
      <div className={`flex flex-col gap-4 ${className}`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <div className="h-7 w-48 bg-gray-200 rounded animate-pulse" />
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 bg-gray-200 rounded-lg animate-pulse" />
              <div className="h-7 w-16 bg-gray-200 rounded-lg animate-pulse" />
              <div className="h-7 w-7 bg-gray-200 rounded-lg animate-pulse" />
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="h-8 w-full sm:w-64 bg-gray-200 rounded-lg animate-pulse" />
            <div className="h-8 w-24 bg-gray-200 rounded-lg animate-pulse" />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="h-9 w-full bg-gray-200 rounded-lg animate-pulse" />
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="grid grid-cols-7 border-b border-gray-200">
            {Array.from({ length: 7 }).map((_, i) => (
              <div
                key={i}
                className="border-r border-gray-200 p-2 last:border-r-0"
              >
                <div className="h-4 bg-gray-200 rounded animate-pulse" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {Array.from({ length: 35 }).map((_, i) => (
              <div
                key={i}
                className="min-h-24 border-b border-r border-gray-200 p-2 last:border-r-0"
              >
                <div className="h-5 w-5 bg-gray-200 rounded-full animate-pulse mb-2" />
                <div className="space-y-1">
                  <div className="h-6 bg-gray-200 rounded animate-pulse" />
                  <div className="h-6 bg-gray-200 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">
            {view === "month" &&
              currentDate.toLocaleDateString("en-US", {
                month: "long",
                year: "numeric",
              })}
            {view === "week" &&
              `Week of ${currentDate.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })}`}
            {view === "day" &&
              currentDate.toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            {view === "list" && "All Events"}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateDate("prev")}
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 transition-colors hover:bg-gray-50"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              Today
            </button>
            <button
              onClick={() => navigateDate("next")}
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 transition-colors hover:bg-gray-50"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="sm:hidden">
            <select
              value={view}
              onChange={(e) => setView(e.target.value as any)}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ff6d27]"
            >
              <option value="month">Month View</option>
              <option value="week">Week View</option>
              <option value="day">Day View</option>
              <option value="list">List View</option>
            </select>
          </div>

          <div className="hidden sm:flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1">
            <button
              onClick={() => setView("month")}
              className={`flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors ${
                view === "month"
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <Calendar className="h-3.5 w-3.5" />
              <span>Month</span>
            </button>
            <button
              onClick={() => setView("week")}
              className={`flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors ${
                view === "week"
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <Grid3x3 className="h-3.5 w-3.5" />
              <span>Week</span>
            </button>
            <button
              onClick={() => setView("day")}
              className={`flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors ${
                view === "day"
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <Clock className="h-3.5 w-3.5" />
              <span>Day</span>
            </button>
            <button
              onClick={() => setView("list")}
              className={`flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors ${
                view === "list"
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <List className="h-3.5 w-3.5" />
              <span>List</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            placeholder="Search events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-9 text-xs text-gray-900 placeholder-gray-400 focus:border-[#ff6d27] focus:outline-none focus:ring-2 focus:ring-[#ff6d27]/20"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-1 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {view === "month" && (
        <MonthView
          currentDate={currentDate}
          events={filteredEvents}
          onEventClick={(event) => {
            setSelectedEvent(event);
            setIsDialogOpen(true);
          }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDrop={handleDrop}
          getColorClasses={getColorClasses}
        />
      )}

      {view === "week" && (
        <WeekView
          currentDate={currentDate}
          events={filteredEvents}
          onEventClick={(event) => {
            setSelectedEvent(event);
            setIsDialogOpen(true);
          }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDrop={handleDrop}
          getColorClasses={getColorClasses}
        />
      )}

      {view === "day" && (
        <DayView
          currentDate={currentDate}
          events={filteredEvents}
          onEventClick={(event) => {
            setSelectedEvent(event);
            setIsDialogOpen(true);
          }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDrop={handleDrop}
          getColorClasses={getColorClasses}
        />
      )}

      {view === "list" && (
        <ListView
          events={filteredEvents}
          onEventClick={(event) => {
            setSelectedEvent(event);
            setIsDialogOpen(true);
          }}
          getColorClasses={getColorClasses}
        />
      )}

      {isDialogOpen && selectedEvent && selectedEvent.meeting && (
        <MeetingDetailsDialog
          meeting={selectedEvent.meeting}
          onClose={() => {
            setIsDialogOpen(false);
            setSelectedEvent(null);
          }}
        />
      )}
    </div>
  );
}

export default function CalendarPage() {
  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="max-w-7xl mx-auto">
        {/* Professional Header Section */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div className="space-y-2">
              <h1 className="text-3xl font-light text-gray-900 tracking-tight">
                Calendar
              </h1>
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-primary rounded-full"></div>
                <p className="text-sm font-light text-gray-500 tracking-wide">
                  View and manage your meetings and events
                </p>
              </div>
            </div>
          </div>
        </div>
        <EventManager />
      </div>
    </div>
  );
}
